import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Bell,
  BellRing,
  ClipboardCheck,
  Cloud,
  Hash,
  Loader2,
  Save,
  ShieldCheck,
  Tags,
  TrendingDown,
  Truck,
  Warehouse,
  Wrench,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  CheckCircle2,
  AlertTriangle,
  FolderTree,
  Layers,
  Settings,
} from 'lucide-react';
import { AssetCloudStorageTab } from './AssetCloudStorageTab';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/Dialog';
import { cn } from '../../../lib/utils';
import {
  formatAssetCode,
  useAssetSettingsStore,
  type AssetSettings,
} from '../../../store/assetSettingsStore';
import {
  useAssetCatalogStore,
  type AssetCategory,
  type AssetType,
  type CategoryField,
} from '../../../store/assetCatalogStore';
import { useAssetStore } from '../../../store/assetStore';
import { useAssetGrant } from '../requests/requestUi';

type SectionKey = keyof AssetSettings;
export type TabId = SectionKey | 'categories' | 'types' | 'storage';

export interface AssetNavGroup {
  label: string;
  items: Array<{
    id: TabId;
    label: string;
    title: string;
    description: string;
    icon: any;
  }>;
}

export const ASSET_SETTINGS_NAV_GROUPS: AssetNavGroup[] = [
  {
    label: 'Asset Catalog',
    items: [
      {
        id: 'numbering',
        label: 'Asset Codes',
        title: 'Asset Codes & Identification',
        description: 'Configure asset numbering rules, code prefixes, auto-increment padding, and barcode symbologies.',
        icon: Hash,
      },
      {
        id: 'types',
        label: 'Asset Types',
        title: 'Asset Types',
        description: 'Manage core asset classifications (Hardware, Software, Network, Cloud, Facilities) stored dynamically.',
        icon: Layers,
      },
      {
        id: 'categories',
        label: 'Categories & Fields',
        title: 'Categories & Custom Schema',
        description: 'Manage device groupings and define dynamic custom schema fields required for each hardware type.',
        icon: Tags,
      },
    ],
  },
  {
    label: 'Lifecycle & Finance',
    items: [
      {
        id: 'warranty',
        label: 'Warranty',
        title: 'Warranty Rules & Lifecycles',
        description: 'Set hardware warranty durations, warning thresholds, preventive maintenance cycles, and RMA requirements.',
        icon: Bell,
      },
      {
        id: 'requisition',
        label: 'Requests',
        title: 'Requests & Approvals Policy',
        description: 'Configure employee requisition limits, multi-tier manager approvals, auto-approval thresholds, and returns.',
        icon: ShieldCheck,
      },
      {
        id: 'depreciation',
        label: 'Depreciation',
        title: 'Depreciation & Book Valuation',
        description: 'Define asset accounting methods, calculation frequencies, fiscal year offsets, and salvage value percentages.',
        icon: TrendingDown,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        id: 'stock',
        label: 'Stock',
        title: 'Inventory & Stock Rules',
        description: 'Configure minimum stock replenishment thresholds, default intake warehouses, and negative inventory rules.',
        icon: Warehouse,
      },
      {
        id: 'maintenance',
        label: 'Maintenance',
        title: 'Maintenance SLA & Repairs',
        description: 'Set standard and critical repair response SLAs, automatic service ticket dispatch, and vendor assignment rules.',
        icon: Wrench,
      },
      {
        id: 'procurement',
        label: 'Procurement',
        title: 'Procurement Settings',
        description: 'Configure purchase order numbering prefixes, required delivery lead times, and receiving inspection workflows.',
        icon: Truck,
      },
      {
        id: 'audit',
        label: 'Audit & Compliance',
        title: 'Physical Verification & Audit Policy',
        description: 'Establish physical audit intervals, digital custody handover signatures, and overdue missing flags.',
        icon: ClipboardCheck,
      },
    ],
  },
  {
    label: 'Platform & System',
    items: [
      {
        id: 'notifications',
        label: 'Alerts & Rules',
        title: 'Alert & Notification Rules',
        description: 'Automate notifications for equipment assignment, returns, lost asset alerts, and administrative email rosters.',
        icon: BellRing,
      },
      {
        id: 'storage',
        label: 'File Storage',
        title: 'ITAM Cloud S3 Storage Vault',
        description: 'Configure enterprise AWS S3 storage for hardware asset condition photos, device manuals, and tax invoices.',
        icon: Cloud,
      },
    ],
  },
];

export const ALL_ASSET_TABS = ASSET_SETTINGS_NAV_GROUPS.flatMap((g) => g.items);

const inputClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary disabled:opacity-60';

const FIELD_TYPES: { value: CategoryField['type']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown / Select' },
  { value: 'boolean', label: 'Yes / No' },
];

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-2 border-b border-border py-3 last:border-0 md:grid-cols-[1fr_280px] md:items-center">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

/**
 * Asset Hub Settings — incorporates configuration for numbering, categories & custom fields,
 * warranty, requisitions, depreciation, stock, maintenance, procurement, and file storage.
 */
export function AssetSettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');

  const {
    settings,
    fetchSettings,
    saveSettings,
    isSaving,
    error,
    storage,
    storageForbidden,
    fetchBackendStorageConfig,
    saveBackendStorageConfig,
    testStorageConnection,
    isTestingStorage,
    storageTestResult,
  } = useAssetSettingsStore();

  const {
    categories,
    categoriesLoaded,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    addCategoryField,
    deleteCategoryField,
    types,
    typesLoaded,
    fetchTypes,
    createType,
    updateType,
    deleteType,
  } = useAssetCatalogStore();

  const canEdit = useAssetGrant('Asset Settings', 'Edit');

  // Tab state synchronized with URL query params
  const [tab, setTabState] = useState<TabId>(() => {
    if (urlTab === 'categories' || urlTab === 'custom_fields') return 'categories';
    if (urlTab === 'types') return 'types';
    if (urlTab && ALL_ASSET_TABS.some((t) => t.id === urlTab)) return urlTab as TabId;
    return 'numbering';
  });

  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);

  const activeTabMeta = useMemo(
    () => ALL_ASSET_TABS.find((i) => i.id === tab),
    [tab]
  );

  const setTab = (newTab: TabId) => {
    setTabState(newTab);
    setHeaderActions(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newTab === 'numbering') next.delete('tab');
        else next.set('tab', newTab);
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (urlTab === 'categories' || urlTab === 'custom_fields') {
      setTabState('categories');
    } else if (urlTab && ALL_ASSET_TABS.some((t) => t.id === urlTab)) {
      setTabState(urlTab as TabId);
    }
  }, [urlTab]);

  const [draft, setDraft] = useState<AssetSettings | null>(null);
  const [storageDraft, setStorageDraft] = useState({ access_key_id: '', secret_access_key: '' });
  const nextAssetCode = useAssetStore((st) => st.nextAssetCode);
  const [serverNext, setServerNext] = useState<string | null>(null);

  // Asset Types Editor States
  const [typeSearch, setTypeSearch] = useState('');
  const [typeEditor, setTypeEditor] = useState<{
    id: number | null;
    name: string;
    description: string;
    icon: string;
    isActive?: boolean;
  } | null>(null);
  const [deletingType, setDeletingType] = useState<AssetType | null>(null);
  const [typeBusy, setTypeBusy] = useState(false);

  // Category & Schema Editor States
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryEditor, setCategoryEditor] = useState<{ id: number | null; name: string; description: string; typeId: number | null } | null>(
    null
  );
  const [fieldsFor, setFieldsFor] = useState<AssetCategory | null>(null);
  const [newField, setNewField] = useState({
    label: '',
    type: 'text' as CategoryField['type'],
    required: false,
    options: '',
  });
  const [deletingCategory, setDeletingCategory] = useState<AssetCategory | null>(null);
  const [categoryBusy, setCategoryBusy] = useState(false);

  useEffect(() => {
    if (settings) {
      nextAssetCode().then(setServerNext).catch(() => setServerNext(null));
    }
  }, [settings, nextAssetCode]);

  useEffect(() => {
    fetchSettings();
    fetchBackendStorageConfig();
    fetchCategories();
    fetchTypes();
  }, [fetchSettings, fetchBackendStorageConfig, fetchCategories, fetchTypes]);

  // Reset the draft whenever the saved settings change
  useEffect(() => {
    if (settings) setDraft(structuredClone(settings));
  }, [settings]);

  // Keep open field manager in sync with store
  const openFields = fieldsFor ? categories.find((c) => c.id === fieldsFor.id) || fieldsFor : null;

  const section = tab !== 'categories' && tab !== 'types' && tab !== 'storage' ? (tab as SectionKey) : null;
  const dirty = useMemo(
    () => !!(section && draft && settings && JSON.stringify(draft[section]) !== JSON.stringify(settings[section])),
    [draft, settings, section]
  );

  const set = <S extends SectionKey>(s: S, key: keyof AssetSettings[S], value: any) =>
    setDraft((d) => (d ? { ...d, [s]: { ...d[s], [key]: value } } : d));
  const num = <S extends SectionKey>(s: S, key: keyof AssetSettings[S]) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(s, key, e.target.value === '' ? 0 : Number(e.target.value));
  const bool = <S extends SectionKey>(s: S, key: keyof AssetSettings[S]) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(s, key, e.target.checked);

  const saveSection = async () => {
    if (!section || !draft) return;
    try {
      await saveSettings(
        { [section]: draft[section] } as any,
        `${activeTabMeta?.label || section} settings saved`
      );
    } catch {
      // toast already shown by store
    }
  };

  // Category actions
  const runCategoryAction = async (fn: () => Promise<any>, okMsg: string) => {
    setCategoryBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      return true;
    } catch (e: any) {
      toast.error(e.message || 'Operation failed');
      return false;
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryEditor?.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    const done = await runCategoryAction(
      () =>
        categoryEditor.id
          ? updateCategory(categoryEditor.id, {
              name: categoryEditor.name.trim(),
              description: categoryEditor.description.trim(),
              type_id: categoryEditor.typeId,
            })
          : createCategory({
              name: categoryEditor.name.trim(),
              description: categoryEditor.description.trim(),
              type_id: categoryEditor.typeId,
            }),
      categoryEditor.id ? 'Category updated successfully' : 'New category created successfully'
    );
    if (done) setCategoryEditor(null);
  };

  const handleAddField = async () => {
    if (!openFields || !newField.label.trim()) {
      toast.error('Field label is required');
      return;
    }
    const done = await runCategoryAction(
      () =>
        addCategoryField(openFields.id, {
          label: newField.label.trim(),
          type: newField.type,
          required: newField.required,
          options:
            newField.type === 'select'
              ? newField.options
                  .split(',')
                  .map((o) => o.trim())
                  .filter(Boolean)
              : undefined,
        }),
      'Schema attribute added'
    );
    if (done) setNewField({ label: '', type: 'text', required: false, options: '' });
  };

  // Type actions
  const runTypeAction = async (fn: () => Promise<any>, okMsg: string) => {
    setTypeBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      return true;
    } catch (e: any) {
      toast.error(e.message || 'Operation failed');
      return false;
    } finally {
      setTypeBusy(false);
    }
  };

  const handleSaveType = async () => {
    if (!typeEditor?.name.trim()) {
      toast.error('Asset type name is required');
      return;
    }
    const done = await runTypeAction(
      () =>
        typeEditor.id
          ? updateType(typeEditor.id, {
              name: typeEditor.name.trim(),
              description: typeEditor.description.trim(),
              icon: typeEditor.icon || 'Layers',
              is_active: typeEditor.isActive !== undefined ? typeEditor.isActive : true,
            })
          : createType({
              name: typeEditor.name.trim(),
              description: typeEditor.description.trim(),
              icon: typeEditor.icon || 'Layers',
            }),
      typeEditor.id ? 'Asset type updated successfully' : 'New asset type created successfully'
    );
    if (done) setTypeEditor(null);
  };

  // Filtered types
  const filteredTypes = useMemo(() => {
    if (!typeSearch.trim()) return types;
    const q = typeSearch.toLowerCase();
    return types.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.code && t.code.toLowerCase().includes(q)) ||
        (t.description && t.description.toLowerCase().includes(q))
    );
  }, [types, typeSearch]);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const q = categorySearch.toLowerCase();
    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.key && c.key.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q))
    );
  }, [categories, categorySearch]);

  if (error && !settings) {
    return (
      <div className="p-6">
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive-strong">
          {error}
        </p>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  const d = draft;

  return (
    <div className="min-h-full space-y-4 overflow-x-hidden bg-background p-4 pb-20 font-sans text-xs text-foreground">
      {/* 2-COLUMN ENTERPRISE LAYOUT: LEFT VERTICAL NAV + RIGHT WORKSPACE */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* 1. LEFT VERTICAL NAVIGATION */}
        <aside className="w-full lg:w-64 shrink-0 rounded-xl border border-border bg-card p-3 shadow-xs space-y-4 lg:sticky lg:top-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-border">
            <div className="size-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Settings className="size-4" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-sm text-foreground">Asset Settings</h2>
              <p className="text-[11px] text-muted-foreground">Single enterprise control panel</p>
            </div>
          </div>

          <nav className="space-y-4" aria-label="Asset Settings Navigation">
            {ASSET_SETTINGS_NAV_GROUPS.map((group) => (
              <div key={group.label} className="space-y-1">
                <div className="px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = tab === item.id;
                    const badge = item.id === 'types' ? types.length : item.id === 'categories' ? categories.length : undefined;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setTab(item.id)}
                        className={cn(
                          'w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer',
                          isActive
                            ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className={cn('size-3.5 shrink-0', isActive ? 'text-primary-foreground' : 'text-muted-foreground')} />
                          <span className="truncate">{item.label}</span>
                        </div>
                        {badge !== undefined && (
                          <span
                            className={cn(
                              'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider',
                              isActive
                                ? 'bg-primary-foreground/20 text-primary-foreground'
                                : 'bg-secondary text-muted-foreground'
                            )}
                          >
                            {badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* 2. RIGHT WORKSPACE */}
        <main className="flex-1 min-w-0 space-y-4 w-full">
          {/* TOP CONTEXTUAL HEADER CARD */}
          <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {activeTabMeta?.icon && (
                <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
                  <activeTabMeta.icon className="size-4.5" />
                </div>
              )}
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display font-semibold text-page-title tracking-tight text-foreground truncate">
                    {activeTabMeta?.title || activeTabMeta?.label || 'Asset Settings'}
                  </h1>
                  {tab === 'types' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                      {types.length} Types
                    </span>
                  )}
                  {tab === 'categories' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                      {categories.length} Categories
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-1 sm:line-clamp-none">
                  {activeTabMeta?.description}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {headerActions}
              {section && (
                <Button onClick={saveSection} disabled={!canEdit || !dirty} isLoading={isSaving} size="sm">
                  <Save className="size-3.5 mr-1.5" /> Save Changes
                </Button>
              )}
              {tab === 'categories' && canEdit && (
                <Button onClick={() => setCategoryEditor({ id: null, name: '', description: '', typeId: null })} size="sm">
                  <Plus className="size-3.5 mr-1.5" /> Add Category
                </Button>
              )}
              {tab === 'types' && canEdit && (
                <Button onClick={() => setTypeEditor({ id: null, name: '', description: '', icon: 'Layers', isActive: true })} size="sm">
                  <Plus className="size-3.5 mr-1.5" /> Add Asset Type
                </Button>
              )}
            </div>
          </div>

          {!canEdit && (
            <p className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-2.5 text-xs text-warning-strong">
              You are viewing these settings in read-only mode. Only asset administrators can modify configurations.
            </p>
          )}

          {/* TAB CONTENTS */}
          <div id="asset-settings-panel" role="tabpanel" className="space-y-4" tabIndex={0}>
            {section && (
              <Card className="min-w-0 p-4 sm:p-5">
                <fieldset disabled={!canEdit} className="contents">
                  {/* TAB: NUMBERING */}
                  {tab === 'numbering' && (
                    <>
                      <div className="mb-4 rounded-lg border border-border bg-secondary/30 p-3 text-xs sm:text-sm text-muted-foreground">
                        {dirty ? (
                          <>
                            Format preview: <span className="font-mono font-semibold text-foreground">{formatAssetCode(d.numbering, 'Laptops')}</span> — save to apply.
                          </>
                        ) : (
                          <>
                            Next code: <span className="font-mono font-semibold text-foreground">{serverNext ?? '…'}</span> — numbers already assigned in the register are never reused.
                          </>
                        )}
                      </div>
              <Row label="Prefix" hint="1–10 letters or digits">
                <input
                  className={inputClass}
                  value={d.numbering.prefix}
                  maxLength={10}
                  onChange={(e) => set('numbering', 'prefix', e.target.value.toUpperCase())}
                />
              </Row>
              <Row label="Separator">
                <select
                  className={inputClass}
                  value={d.numbering.separator}
                  onChange={(e) => set('numbering', 'separator', e.target.value)}
                >
                  <option value="">None (TBA10001)</option>
                  <option value="-">Hyphen (TBA-10001)</option>
                  <option value="_">Underscore (TBA_10001)</option>
                  <option value="/">Slash (TBA/10001)</option>
                </select>
              </Row>
              <Row label="Digits" hint="Numbers are zero-padded to this length">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  max={10}
                  value={d.numbering.padding_digits}
                  onChange={num('numbering', 'padding_digits')}
                />
              </Row>
              <Row label="Start numbering at" hint="Used only when it is higher than the last code used">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.numbering.next_number}
                  onChange={num('numbering', 'next_number')}
                />
              </Row>
              <Row label="Include category code" hint="e.g. TBA-LAP-10001">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.numbering.include_category_code}
                  onChange={bool('numbering', 'include_category_code')}
                />
              </Row>
              <Row label="Include year" hint="Embeds active 4-digit year, e.g. TBA-2026-10001">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.numbering.include_year}
                  onChange={bool('numbering', 'include_year')}
                />
              </Row>
              <Row label="Allow manual override" hint="Permits technicians to enter legacy or manufacturer tags">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.numbering.allow_manual_override}
                  onChange={bool('numbering', 'allow_manual_override')}
                />
              </Row>
              <Row label="Barcode symbology" hint="Standard encoding for physical asset labels">
                <select
                  className={inputClass}
                  value={d.numbering.barcode_symbology}
                  onChange={(e) => set('numbering', 'barcode_symbology', e.target.value as any)}
                >
                  <option value="Code128">Code 128 (Standard 1D Barcode)</option>
                  <option value="QR Code">QR Code (2D Matrix)</option>
                  <option value="DataMatrix">DataMatrix (Compact 2D)</option>
                  <option value="Code39">Code 39 (Alphanumeric 1D)</option>
                </select>
              </Row>
            </>
          )}

          {/* TAB: WARRANTY */}
          {tab === 'warranty' && (
            <>
              <Row label="Default warranty (months)" hint="Applied to assets created when a purchase order is received">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.warranty.default_hardware_warranty_months}
                  onChange={num('warranty', 'default_hardware_warranty_months')}
                />
              </Row>
              <Row label="Flag warranties ending within (days)" hint="Used by the Warranty tracker and dashboard">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.warranty.warranty_alert_threshold_days}
                  onChange={num('warranty', 'warranty_alert_threshold_days')}
                />
              </Row>
              <Row label="Preventive maintenance every (days)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.warranty.preventive_maintenance_cycle_days}
                  onChange={num('warranty', 'preventive_maintenance_cycle_days')}
                />
              </Row>
              <Row label="End-of-life notice (months)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.warranty.eol_notification_months}
                  onChange={num('warranty', 'eol_notification_months')}
                />
              </Row>
              <Row label="Warranty grace period (days)" hint="Buffer period after expiry before status changes">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.warranty.grace_period_days}
                  onChange={num('warranty', 'grace_period_days')}
                />
              </Row>
              <Row label="Auto-notify vendor on warranty claim" hint="Send notification to supplier when warranty claim logged">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.warranty.auto_notify_vendor_on_warranty_claim}
                  onChange={bool('warranty', 'auto_notify_vendor_on_warranty_claim')}
                />
              </Row>
              <Row label="Auto-create ticket on expiry" hint="Open review ticket in Support Hub 30 days before warranty ends">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.warranty.auto_create_ticket_on_warranty_expiry}
                  onChange={bool('warranty', 'auto_create_ticket_on_warranty_expiry')}
                />
              </Row>
              <Row label="Require vendor RMA number" hint="Enforce RMA tracking ID for all external repair dispatches">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.warranty.require_vendor_rma_number}
                  onChange={bool('warranty', 'require_vendor_rma_number')}
                />
              </Row>
            </>
          )}

          {/* TAB: REQUISITION */}
          {tab === 'requisition' && (
            <>
              <Row label="Manager approval required">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.requisition.require_manager_approval}
                  onChange={bool('requisition', 'require_manager_approval')}
                />
              </Row>
              <Row label="Allow employee self-selection" hint="Permits requesting specific hardware models from catalog">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.requisition.allow_employee_self_selection}
                  onChange={bool('requisition', 'allow_employee_self_selection')}
                />
              </Row>
              <Row label="Auto-approve low-cost requests">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.requisition.auto_approve_low_cost}
                  onChange={bool('requisition', 'auto_approve_low_cost')}
                />
              </Row>
              <Row label="Low-cost limit ($)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.requisition.low_cost_threshold_amount}
                  onChange={num('requisition', 'low_cost_threshold_amount')}
                />
              </Row>
              <Row label="Extra approval above ($)" hint="0 means no extra approval tier">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.requisition.require_it_director_approval_above}
                  onChange={num('requisition', 'require_it_director_approval_above')}
                />
              </Row>
              <Row label="Max active requests per user" hint="Limit open hardware requisitions per employee">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.requisition.max_active_requests_per_user}
                  onChange={num('requisition', 'max_active_requests_per_user')}
                />
              </Row>
              <Row label="Auto-create support ticket" hint="Route approved requisitions directly to Support fulfillment">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.requisition.auto_create_support_ticket}
                  onChange={bool('requisition', 'auto_create_support_ticket')}
                />
              </Row>
              <Row label="Require return before replacement" hint="Old device must be surrendered before replacement is issued">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.requisition.require_return_for_replacement}
                  onChange={bool('requisition', 'require_return_for_replacement')}
                />
              </Row>
              <Row label="Restricted categories" hint="Requests for these require administrator review">
                <select
                  multiple
                  className="min-h-24 w-full rounded-lg border border-input bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
                  value={d.requisition.restricted_categories}
                  onChange={(e) =>
                    set('requisition', 'restricted_categories', Array.from(e.target.selectedOptions).map((o) => o.value))
                  }
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Row>
            </>
          )}

          {/* TAB: DEPRECIATION */}
          {tab === 'depreciation' && (
            <>
              <Row label="Method">
                <select
                  className={inputClass}
                  value={d.depreciation.method}
                  onChange={(e) => set('depreciation', 'method', e.target.value as any)}
                >
                  {['Straight Line', 'Declining Balance', 'Sum of Years', 'No Depreciation'].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </Row>
              <Row label="Calculation frequency" hint="Schedule for asset book value updates">
                <select
                  className={inputClass}
                  value={d.depreciation.depreciation_frequency}
                  onChange={(e) => set('depreciation', 'depreciation_frequency', e.target.value as any)}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Annually">Annually</option>
                </select>
              </Row>
              <Row label="Fiscal year start month" hint="Accounting year opening month">
                <select
                  className={inputClass}
                  value={d.depreciation.fiscal_year_start_month}
                  onChange={(e) => set('depreciation', 'fiscal_year_start_month', e.target.value)}
                >
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Row>
              <Row label="Default salvage value (% of cost)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  max={100}
                  value={d.depreciation.default_salvage_percentage}
                  onChange={num('depreciation', 'default_salvage_percentage')}
                />
              </Row>
              <Row label="Minimum depreciable cost ($)" hint="Assets costing less are expensed immediately">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.depreciation.minimum_depreciable_cost}
                  onChange={num('depreciation', 'minimum_depreciable_cost')}
                />
              </Row>
              <Row label="Laptop useful life (years)">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.depreciation.laptop_useful_life_years}
                  onChange={num('depreciation', 'laptop_useful_life_years')}
                />
              </Row>
              <Row label="Server useful life (years)">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.depreciation.server_useful_life_years}
                  onChange={num('depreciation', 'server_useful_life_years')}
                />
              </Row>
              <Row label="Mobile & tablet useful life (years)">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.depreciation.mobile_useful_life_years}
                  onChange={num('depreciation', 'mobile_useful_life_years')}
                />
              </Row>
              <Row label="Peripherals & docks useful life (years)">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.depreciation.peripheral_useful_life_years}
                  onChange={num('depreciation', 'peripheral_useful_life_years')}
                />
              </Row>
            </>
          )}

          {/* TAB: STOCK */}
          {tab === 'stock' && (
            <>
              <Row label="Low stock threshold">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.stock.low_stock_threshold}
                  onChange={num('stock', 'low_stock_threshold')}
                />
              </Row>
              <Row label="Reorder quantity">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.stock.reorder_quantity}
                  onChange={num('stock', 'reorder_quantity')}
                />
              </Row>
              <Row label="Default delivery location" hint="Standard intake warehouse or site">
                <input
                  className={inputClass}
                  value={d.stock.default_delivery_location}
                  maxLength={100}
                  onChange={(e) => set('stock', 'default_delivery_location', e.target.value)}
                />
              </Row>
              <Row label="Auto-notify on low stock" hint="Broadcast alerts when counts reach threshold">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.stock.auto_notify_low_stock}
                  onChange={bool('stock', 'auto_notify_low_stock')}
                />
              </Row>
              <Row label="Allow negative inventory" hint="Permits assignments when tracked count is zero">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.stock.allow_negative_inventory}
                  onChange={bool('stock', 'allow_negative_inventory')}
                />
              </Row>
            </>
          )}

          {/* TAB: MAINTENANCE */}
          {tab === 'maintenance' && (
            <>
              <Row label="Standard response target (hours)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.maintenance.response_sla_hours}
                  onChange={num('maintenance', 'response_sla_hours')}
                />
              </Row>
              <Row label="Critical asset SLA (hours)" hint="Expedited SLA for servers and network infrastructure">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.maintenance.critical_asset_sla_hours}
                  onChange={num('maintenance', 'critical_asset_sla_hours')}
                />
              </Row>
              <Row label="Resolution target (days)">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.maintenance.resolution_sla_days}
                  onChange={num('maintenance', 'resolution_sla_days')}
                />
              </Row>
              <Row label="Auto-create maintenance ticket" hint="Log a service ticket in Support Hub upon maintenance schedule">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.maintenance.auto_create_maintenance_ticket}
                  onChange={bool('maintenance', 'auto_create_maintenance_ticket')}
                />
              </Row>
              <Row label="Require vendor for external repairs" hint="Require vendor selection for dispatched repair work">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.maintenance.require_vendor_for_repairs}
                  onChange={bool('maintenance', 'require_vendor_for_repairs')}
                />
              </Row>
            </>
          )}

          {/* TAB: PROCUREMENT */}
          {tab === 'procurement' && (
            <>
              <Row
                label="PO number prefix"
                hint={`Numbers look like ${d.procurement.po_prefix || 'PO-'}${new Date().getFullYear()}-0001`}
              >
                <input
                  className={inputClass}
                  value={d.procurement.po_prefix}
                  maxLength={20}
                  onChange={(e) => set('procurement', 'po_prefix', e.target.value)}
                />
              </Row>
              <Row label="Currency" hint="3-letter code, e.g. USD, EUR, INR">
                <input
                  className={inputClass}
                  value={d.procurement.default_currency}
                  maxLength={3}
                  onChange={(e) => set('procurement', 'default_currency', e.target.value.toUpperCase())}
                />
              </Row>
              <Row label="Approval required above" hint="0 means no approval threshold">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  value={d.procurement.approval_required_above}
                  onChange={num('procurement', 'approval_required_above')}
                />
              </Row>
              <Row label="Default payment terms" hint="Default supplier terms populated on purchase orders">
                <select
                  className={inputClass}
                  value={d.procurement.default_payment_terms}
                  onChange={(e) => set('procurement', 'default_payment_terms', e.target.value)}
                >
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="Advance">Advance</option>
                </select>
              </Row>
              <Row label="Auto-create receiving ticket" hint="Raise a receipt ticket in Support Hub upon PO submission">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.procurement.auto_create_ticket_on_po}
                  onChange={bool('procurement', 'auto_create_ticket_on_po')}
                />
              </Row>
              <Row label="Purchase terms & conditions" hint="Default clauses appended to purchase orders">
                <textarea
                  className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  value={d.procurement.terms_and_conditions}
                  maxLength={1000}
                  onChange={(e) => set('procurement', 'terms_and_conditions', e.target.value)}
                />
              </Row>
            </>
          )}

          {/* TAB: AUDIT & COMPLIANCE */}
          {tab === 'audit' && (
            <>
              <Row label="Audit cycle interval (days)" hint="Frequency for complete physical inventory checks (e.g. 180 days)">
                <input
                  className={inputClass}
                  type="number"
                  min={30}
                  value={d.audit.audit_cycle_days}
                  onChange={num('audit', 'audit_cycle_days')}
                />
              </Row>
              <Row label="Require digital custody signature" hint="Require staff acknowledgement signature upon device handover">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.audit.require_custody_signature}
                  onChange={bool('audit', 'require_custody_signature')}
                />
              </Row>
              <Row label="Allow remote employee self-audit" hint="Permits remote employees to verify serial and status via portal">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.audit.allow_employee_self_audit}
                  onChange={bool('audit', 'allow_employee_self_audit')}
                />
              </Row>
              <Row label="Flag missing after overdue (days)" hint="Automatically mark unverified hardware as 'Flagged Missing'">
                <input
                  className={inputClass}
                  type="number"
                  min={1}
                  value={d.audit.flag_missing_after_days}
                  onChange={num('audit', 'flag_missing_after_days')}
                />
              </Row>
            </>
          )}

          {/* TAB: NOTIFICATIONS & ALERT RULES */}
          {tab === 'notifications' && (
            <>
              <Row label="Notify employee on assignment" hint="Send confirmation and asset details to employee upon assignment">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.notifications.notify_employee_on_assignment}
                  onChange={bool('notifications', 'notify_employee_on_assignment')}
                />
              </Row>
              <Row label="Notify employee on return" hint="Issue formal return confirmation receipt when returned to stock">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.notifications.notify_employee_on_return}
                  onChange={bool('notifications', 'notify_employee_on_return')}
                />
              </Row>
              <Row label="Notify manager on asset loss" hint="Alert reporting manager and IT security when asset reported lost">
                <input
                  type="checkbox"
                  className="cursor-pointer accent-primary h-4 w-4 rounded"
                  checked={d.notifications.notify_manager_on_asset_loss}
                  onChange={bool('notifications', 'notify_manager_on_asset_loss')}
                />
              </Row>
              <Row label="IT alert email distribution" hint="Comma-separated emails for IT escalations">
                <input
                  className={inputClass}
                  placeholder="it-ops@example.com, asset-admin@example.com"
                  value={d.notifications.email_alert_recipients?.join(', ') || ''}
                  onChange={(e) =>
                    set(
                      'notifications',
                      'email_alert_recipients',
                      e.target.value.split(',').map((x) => x.trim()).filter(Boolean)
                    )
                  }
                />
              </Row>
            </>
          )}
        </fieldset>
      </Card>
    )}

        {/* TAB: REDESIGNED CATEGORIES & CUSTOM SCHEMA FIELDS */}
        {tab === 'categories' && (
          <div className="space-y-4">
            {/* Quick Filter & Stats Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-secondary/20 p-3">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Filter categories by name or description..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-card pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1 font-medium text-foreground">
                  <Tags className="size-3.5 text-primary" /> {categories.length} Categories
                </span>
                <span>•</span>
                <span>
                  {categories.reduce((acc, c) => acc + c.fields.length, 0)} Total Schema Fields
                </span>
              </div>
            </div>

            {/* Categories Grid */}
            {categories.length === 0 ? (
              <EmptyState
                icon={<Tags className="size-6" />}
                title={categoriesLoaded ? 'No categories defined yet' : 'Loading categories…'}
                description="Add categories like Laptops, Monitors, or Servers to organize your inventory."
                action={
                  canEdit ? (
                    <Button size="sm" onClick={() => setCategoryEditor({ id: null, name: '', description: '', typeId: null })}>
                      <Plus className="size-4 mr-1.5" /> Add Category
                    </Button>
                  ) : undefined
                }
              />
            ) : filteredCategories.length === 0 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">
                No categories match &quot;{categorySearch}&quot;.
              </p>
            ) : (
              <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredCategories.map((c) => (
                  <Card
                    key={c.id}
                    className="flex flex-col justify-between p-4 border border-border hover:border-border/80 transition-all shadow-xs"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="truncate font-display text-card-title text-foreground">
                            {c.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[11px] font-mono text-muted-foreground">
                              key: {c.key || c.name.slice(0, 3).toUpperCase()}
                            </span>
                            <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                              {c.assetCount} {c.assetCount === 1 ? 'asset' : 'assets'}
                            </Badge>
                          </div>
                        </div>

                        {canEdit && (
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`Edit ${c.name}`}
                              onClick={() =>
                                setCategoryEditor({
                                  id: c.id,
                                  name: c.name,
                                  description: c.description || '',
                                  typeId: c.typeId,
                                })
                              }
                            >
                              <Pencil className="size-3.5 text-muted-foreground hover:text-foreground" />
                            </Button>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`Delete ${c.name}`}
                              onClick={() => setDeletingCategory(c)}
                            >
                              <Trash2 className="size-3.5 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <p className="mt-2 text-xs text-muted-foreground line-clamp-2 min-h-[32px]">
                        {c.description || 'No description provided.'}
                      </p>

                      {/* Custom Fields Preview */}
                      <div className="mt-3 pt-2.5 border-t border-border/60">
                        <div className="flex items-center justify-between text-[11px] font-medium text-muted-foreground mb-1.5">
                          <span>Custom Attributes</span>
                          <span className="font-mono">{c.fields.length}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 min-h-[26px]">
                          {c.fields.length === 0 ? (
                            <span className="text-[11px] text-muted-foreground italic">
                              No custom fields defined.
                            </span>
                          ) : (
                            c.fields.map((f) => (
                              <Badge key={f.id} variant="muted" className="text-[10px] py-0.5">
                                {f.label}
                                {f.required ? ' *' : ''}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <Button
                      size="xs"
                      variant="secondary"
                      className="mt-3.5 w-full justify-center text-xs"
                      onClick={() => setFieldsFor(c)}
                    >
                      <FolderTree className="size-3.5 mr-1" /> Manage Fields ({c.fields.length})
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: ASSET TYPES */}
        {tab === 'types' && (
          <div className="space-y-4">
            {/* Quick Filter & Stats Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-secondary/20 p-3">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Filter asset types by name or description..."
                  value={typeSearch}
                  onChange={(e) => setTypeSearch(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-card pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1 font-medium text-foreground">
                  <Layers className="size-3.5 text-primary" /> {types.length} Asset Types
                </span>
                <span>•</span>
                <span>
                  {types.filter((t) => t.isActive).length} Active
                </span>
              </div>
            </div>

            {/* Asset Types Grid */}
            {types.length === 0 ? (
              <EmptyState
                icon={<Layers className="size-6" />}
                title={typesLoaded ? 'No asset types defined' : 'Loading asset types…'}
                description="Asset types define top-level categories such as Hardware, Software, Cloud, or Network."
                action={
                  canEdit ? (
                    <Button size="sm" onClick={() => setTypeEditor({ id: null, name: '', description: '', icon: 'Layers', isActive: true })}>
                      <Plus className="size-4 mr-1.5" /> Add Asset Type
                    </Button>
                  ) : undefined
                }
              />
            ) : filteredTypes.length === 0 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">
                No asset types match &quot;{typeSearch}&quot;.
              </p>
            ) : (
              <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
                {filteredTypes.map((t) => (
                  <Card
                    key={t.id}
                    className="flex flex-col justify-between p-4 border border-border hover:border-border/80 transition-all shadow-xs"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-md bg-primary/10 text-primary">
                              <Layers className="size-4" />
                            </div>
                            <h3 className="truncate font-display text-card-title text-foreground font-semibold">
                              {t.name}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[11px] font-mono text-muted-foreground">
                              code: {t.code}
                            </span>
                            <Badge
                              variant={t.isActive ? 'success' : 'secondary'}
                              className="text-[10px] py-0 px-1.5"
                            >
                              {t.isActive ? 'Active' : 'Disabled'}
                            </Badge>
                            <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                              {t.assetCount} {t.assetCount === 1 ? 'asset' : 'assets'}
                            </Badge>
                          </div>
                        </div>

                        {canEdit && (
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`Edit ${t.name}`}
                              onClick={() =>
                                setTypeEditor({
                                  id: t.id,
                                  name: t.name,
                                  description: t.description || '',
                                  icon: t.icon || 'Layers',
                                  isActive: t.isActive,
                                })
                              }
                            >
                              <Pencil className="size-3.5 text-muted-foreground hover:text-foreground" />
                            </Button>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              aria-label={`Delete ${t.name}`}
                              className="text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeletingType(t)}
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <p className="mt-2.5 text-xs text-muted-foreground line-clamp-2">
                        {t.description || 'No description provided.'}
                      </p>
                    </div>

                    <div className="mt-3.5 pt-2.5 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Status</span>
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() =>
                            runTypeAction(
                              () => updateType(t.id, { is_active: !t.isActive }),
                              `Asset type "${t.name}" ${t.isActive ? 'disabled' : 'enabled'}`
                            )
                          }
                          className={`text-xs font-medium cursor-pointer transition-colors ${
                            t.isActive ? 'text-success hover:underline' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {t.isActive ? 'Active (Click to disable)' : 'Disabled (Click to enable)'}
                        </button>
                      ) : (
                        <span>{t.isActive ? 'Active' : 'Disabled'}</span>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB: S3 CLOUD STORAGE CONFIGURATION */}
        {tab === 'storage' && <AssetCloudStorageTab setHeaderActions={setHeaderActions} />}
          </div>
        </main>
      </div>

      {/* Modal: Category Editor (Add / Edit) */}
      <Dialog open={!!categoryEditor} onOpenChange={(o) => !o && setCategoryEditor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{categoryEditor?.id ? 'Edit Category' : 'Create New Category'}</DialogTitle>
            <DialogDescription>
              {categoryEditor?.id
                ? 'Renaming this category automatically cascades to all assigned assets.'
                : 'Define a category to classify hardware and apply custom schema fields.'}
            </DialogDescription>
          </DialogHeader>
          {categoryEditor && (
            <div className="space-y-3 py-2 text-sm">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Category Name <span className="text-destructive">*</span>
                </label>
                <input
                  className={inputClass}
                  placeholder="e.g. Laptops, Workstations, Network Switches"
                  value={categoryEditor.name}
                  maxLength={100}
                  onChange={(e) => setCategoryEditor({ ...categoryEditor, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Asset Type</label>
                <select
                  className={inputClass}
                  value={categoryEditor.typeId ?? ''}
                  onChange={(e) =>
                    setCategoryEditor({ ...categoryEditor, typeId: e.target.value ? Number(e.target.value) : null })
                  }
                >
                  <option value="">All types (universal)</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Controls which asset type shows this category in the register form. Leave as "All types" to show it everywhere.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Description</label>
                <textarea
                  className="min-h-18 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="Describe what assets belong in this category..."
                  value={categoryEditor.description}
                  maxLength={1000}
                  onChange={(e) => setCategoryEditor({ ...categoryEditor, description: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCategoryEditor(null)} disabled={categoryBusy}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} isLoading={categoryBusy}>
              {categoryEditor?.id ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Custom Fields Manager for Category */}
      <Dialog open={!!openFields} onOpenChange={(o) => !o && setFieldsFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderTree className="size-5 text-primary" /> {openFields?.name}: Custom Schema Fields
            </DialogTitle>
            <DialogDescription>
              Configure extra attributes required when adding or requesting assets in the {openFields?.name} category.
            </DialogDescription>
          </DialogHeader>

          {openFields && (
            <div className="space-y-4 py-2 text-sm">
              {/* Existing Fields List */}
              <div>
                <h4 className="text-xs font-semibold text-foreground mb-2">Configured Attributes</h4>
                {openFields.fields.length === 0 ? (
                  <p className="rounded-lg border border-border/80 bg-secondary/20 p-3 text-xs text-muted-foreground italic">
                    No custom fields configured for this category yet. Use the form below to add attributes.
                  </p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border bg-card">
                    {openFields.fields.map((f) => (
                      <li key={f.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-xs">
                        <div className="min-w-0">
                          <span className="font-medium text-foreground">
                            {f.label}
                            {f.required && <span className="text-destructive font-bold ml-0.5">*</span>}
                          </span>
                          <div className="flex items-center gap-2 mt-0.5 text-muted-foreground text-[11px]">
                            <Badge variant="secondary" className="text-[10px] py-0 px-1 font-mono">
                              {FIELD_TYPES.find((t) => t.value === f.type)?.label || f.type}
                            </Badge>
                            {f.options.length > 0 && (
                              <span className="truncate">Options: {f.options.join(', ')}</span>
                            )}
                          </div>
                        </div>
                        {canEdit && (
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Remove ${f.label}`}
                            disabled={categoryBusy}
                            onClick={() =>
                              runCategoryAction(
                                () => deleteCategoryField(openFields.id, f.id),
                                `Attribute "${f.label}" removed`
                              )
                            }
                          >
                            <X className="size-3.5 text-muted-foreground hover:text-destructive" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Add Attribute Section */}
              {canEdit && (
                <div className="rounded-lg border border-border bg-secondary/15 p-3.5 space-y-3">
                  <h4 className="text-xs font-semibold text-foreground">Add Custom Attribute</h4>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                        Attribute Label <span className="text-destructive">*</span>
                      </label>
                      <input
                        className={inputClass}
                        placeholder="e.g. RAM, Screen Size, Battery Health"
                        value={newField.label}
                        maxLength={100}
                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-medium text-muted-foreground mb-1">Data Type</label>
                      <select
                        className={inputClass}
                        value={newField.type}
                        onChange={(e) => setNewField({ ...newField, type: e.target.value as CategoryField['type'] })}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {newField.type === 'select' && (
                      <div className="sm:col-span-2">
                        <label className="block text-[11px] font-medium text-muted-foreground mb-1">
                          Dropdown Options (Comma separated)
                        </label>
                        <input
                          className={inputClass}
                          placeholder="e.g. 16GB, 32GB, 64GB"
                          value={newField.options}
                          onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                        />
                      </div>
                    )}

                    <div className="sm:col-span-2 flex items-center justify-between pt-1">
                      <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={newField.required}
                          onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                          className="rounded border-input text-primary focus:ring-primary"
                        />
                        Required Attribute
                      </label>

                      <Button size="xs" onClick={handleAddField} isLoading={categoryBusy}>
                        <Plus className="size-3.5 mr-1" /> Add Attribute
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setFieldsFor(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Delete Category Confirmation */}
      <Dialog open={!!deletingCategory} onOpenChange={(o) => !o && setDeletingCategory(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Category &quot;{deletingCategory?.name}&quot;?</DialogTitle>
            <DialogDescription>
              {deletingCategory && deletingCategory.assetCount > 0 ? (
                <span className="text-destructive font-medium block">
                  Cannot delete: {deletingCategory.assetCount} asset(s) are currently categorized as &quot;{deletingCategory.name}&quot;.
                  Reassign them to another category first.
                </span>
              ) : (
                'Are you sure? The category and all its configured custom schema attributes will be permanently removed.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeletingCategory(null)} disabled={categoryBusy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              isLoading={categoryBusy}
              disabled={!deletingCategory || deletingCategory.assetCount > 0}
              onClick={async () => {
                if (
                  deletingCategory &&
                  (await runCategoryAction(
                    () => deleteCategory(deletingCategory.id),
                    `Category "${deletingCategory.name}" deleted`
                  ))
                ) {
                  setDeletingCategory(null);
                }
              }}
            >
              Delete Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Asset Type Editor (Add / Edit) */}
      <Dialog open={!!typeEditor} onOpenChange={(o) => !o && setTypeEditor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{typeEditor?.id ? 'Edit Asset Type' : 'Create New Asset Type'}</DialogTitle>
            <DialogDescription>
              {typeEditor?.id
                ? 'Update classification name and description.'
                : 'Define a core asset classification (e.g. Hardware, Software, Network, Facilities).'}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveType();
            }}
            className="space-y-4 py-2"
          >
            <div>
              <label className="text-xs font-medium text-foreground">Type Name *</label>
              <input
                type="text"
                required
                placeholder="e.g. Virtual Machine, Fleet Vehicle, Telecom"
                value={typeEditor?.name || ''}
                onChange={(e) => setTypeEditor((p) => (p ? { ...p, name: e.target.value } : null))}
                className="mt-1 h-9 w-full rounded-md border border-input bg-card px-3 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground">Description</label>
              <textarea
                rows={2}
                placeholder="Brief summary of assets falling under this type..."
                value={typeEditor?.description || ''}
                onChange={(e) => setTypeEditor((p) => (p ? { ...p, description: e.target.value } : null))}
                className="mt-1 w-full rounded-md border border-input bg-card p-2.5 text-xs text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>

            {typeEditor?.id && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="type-is-active"
                  checked={typeEditor.isActive ?? true}
                  onChange={(e) => setTypeEditor((p) => (p ? { ...p, isActive: e.target.checked } : null))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <label htmlFor="type-is-active" className="text-xs font-medium text-foreground cursor-pointer">
                  Active (available when creating or editing assets)
                </label>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="secondary" onClick={() => setTypeEditor(null)} disabled={typeBusy}>
                Cancel
              </Button>
              <Button type="submit" isLoading={typeBusy} disabled={!typeEditor?.name.trim()}>
                {typeEditor?.id ? 'Save Changes' : 'Create Asset Type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: Delete Asset Type Confirmation */}
      <Dialog open={!!deletingType} onOpenChange={(o) => !o && setDeletingType(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Asset Type &quot;{deletingType?.name}&quot;?</DialogTitle>
            <DialogDescription>
              {deletingType && deletingType.assetCount > 0 ? (
                <span className="text-destructive font-medium block">
                  Cannot delete: {deletingType.assetCount} asset(s) are currently categorized under &quot;{deletingType.name}&quot;.
                  Reassign them to another type first, or disable this type instead.
                </span>
              ) : (
                'Are you sure you want to delete this asset type? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeletingType(null)} disabled={typeBusy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              isLoading={typeBusy}
              disabled={!deletingType || deletingType.assetCount > 0}
              onClick={async () => {
                if (
                  deletingType &&
                  (await runTypeAction(
                    () => deleteType(deletingType.id),
                    `Asset type "${deletingType.name}" deleted`
                  ))
                ) {
                  setDeletingType(null);
                }
              }}
            >
              Delete Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
